import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { FiCalendar, FiEdit2, FiFlag, FiPaperclip, FiPlus, FiTrash2 } from 'react-icons/fi'
import { ConfirmDelete, MwButton, MwSpinnerButton } from '../ui'
import { getStatusCategory } from '../../constants/ticket'
import { apiClient, buildAuthRequestConfig } from '../../utils/apiClient'
import { formatDate, getSprintPhase, SPRINT_PHASE_META } from '../../utils/projectHelpers'
import { buildAttachmentNameMap, deleteUploadedFiles, getAttachmentInfo, normalizeAttachmentList, toAttachmentNamesPayload } from '../../utils/attachments'
import { arabicValueForEdit, localizedText } from '../../utils/localized'
import LangSwitch, { withLangLabel } from '../ui/LangSwitch'
import ProjectAttachmentsField from './ProjectAttachmentsField'

// الإنجاز في الدورة = التذاكر المكتملة والمغلقة من كل تذاكرها.
const DONE_CATEGORIES = ['completed', 'closed'];

const toDateInputValue = (raw) => {
    const value = formatDate(raw);
    return value === '-' ? '' : value;
};

// عدد التذاكر المنجزة في الدورة (مكتملة / مغلقة) من عدّادات الحالات.
export const countDone = (statusCounts, statuses) => Object.entries(statusCounts || {}).reduce(
    (acc, [statusId, count]) => (DONE_CATEGORIES.includes(getStatusCategory(statuses, statusId)) ? acc + Number(count || 0) : acc),
    0
);

const SprintFormModal = ({ isAr, sprint, nextNumber, saving, error, onClose, onSave }) => {
    const [title, setTitle] = useState(arabicValueForEdit(sprint, 'title'));
    const [description, setDescription] = useState(arabicValueForEdit(sprint, 'description'));
    const [titleEn, setTitleEn] = useState(sprint?.titleEn || '');
    const [descriptionEn, setDescriptionEn] = useState(sprint?.descriptionEn || '');
    const [formLang, setFormLang] = useState(isAr ? 'ar' : 'en');
    // مرفقات الدورة بنفس موديول مرفقات التذاكر والمشاريع.
    const [initialAttachments] = useState(() => normalizeAttachmentList(sprint?.attachments));
    const [attachments, setAttachments] = useState(initialAttachments);
    const [attachmentNameMap, setAttachmentNameMap] = useState(() => buildAttachmentNameMap(sprint?.attachmentNames));
    const [attachmentsBusy, setAttachmentsBusy] = useState(false);
    // الإلغاء بيمسح الملفات اللي اترفعت في الفورم ده ومااتحفظتش.
    const closeForm = () => {
        const unsaved = attachments.filter((url) => !initialAttachments.includes(url));
        if (unsaved.length) deleteUploadedFiles(unsaved).catch(() => {});
        onClose?.();
    };
    const [startAt, setStartAt] = useState(toDateInputValue(sprint?.startAt));
    const [endAt, setEndAt] = useState(toDateInputValue(sprint?.endAt));
    const datesInvalid = Boolean(startAt && endAt && endAt < startAt);
    const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', background: '#fff' };
    const labelStyle = { fontSize: 11, fontWeight: 800, color: '#64748b', marginBottom: 4, display: 'block' };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(51,65,85,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={closeForm}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 640, maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,.25)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: 14, fontWeight: 900, color: '#0f172a' }}>
                        {sprint ? (isAr ? 'تعديل الدورة' : 'Edit sprint') : (isAr ? 'إنشاء دورة جديدة' : 'New sprint')}
                    </span>
                    <span style={{ padding: '3px 10px', borderRadius: 999, background: '#ecfeff', color: '#0e7490', fontSize: 11, fontWeight: 900 }}>
                        {isAr ? 'دورة' : 'Sprint'} {sprint ? sprint.sprintNumber : nextNumber}
                        {!sprint && <span style={{ fontWeight: 600 }}> · {isAr ? 'ترتيب تلقائي' : 'auto number'}</span>}
                    </span>
                </div>
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, background: '#f8fafc', overflowY: 'auto' }}>
                    {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: '#fee2e2', color: '#b91c1c', fontSize: 12, fontWeight: 700 }}>{error}</div>}
                    <LangSwitch
                        value={formLang}
                        onChange={setFormLang}
                        isAr={isAr}
                        filled={{ ar: Boolean(title.trim() || description.trim()), en: Boolean(titleEn.trim() || descriptionEn.trim()) }}
                    />
                    <div>
                        <label style={labelStyle}>{withLangLabel(isAr ? 'اسم الدورة (اختياري)' : 'Sprint name (optional)', formLang, isAr)}</label>
                        <input
                            style={inputStyle}
                            dir={formLang === 'ar' ? 'rtl' : 'ltr'}
                            value={formLang === 'ar' ? title : titleEn}
                            onChange={(e) => (formLang === 'ar' ? setTitle : setTitleEn)(e.target.value)}
                            placeholder={formLang === 'ar' ? 'مثال: موديول المحاسبة' : 'e.g. Accounting module'}
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>{withLangLabel(isAr ? 'الوصف' : 'Description', formLang, isAr)}</label>
                        <textarea
                            style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }}
                            dir={formLang === 'ar' ? 'rtl' : 'ltr'}
                            value={formLang === 'ar' ? description : descriptionEn}
                            onChange={(e) => (formLang === 'ar' ? setDescription : setDescriptionEn)(e.target.value)}
                            placeholder={formLang === 'ar' ? 'أهداف ونطاق الدورة...' : 'Sprint goals and scope...'}
                        />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>{isAr ? 'تاريخ البداية' : 'Start date'}</label>
                            <input type="date" style={inputStyle} value={startAt} onChange={(e) => setStartAt(e.target.value)} />
                        </div>
                        <div>
                            <label style={labelStyle}>{isAr ? 'تاريخ النهاية' : 'End date'}</label>
                            <input type="date" style={{ ...inputStyle, borderColor: datesInvalid ? '#ef4444' : '#e2e8f0' }} value={endAt} min={startAt || undefined} onChange={(e) => setEndAt(e.target.value)} />
                        </div>
                    </div>
                    {datesInvalid && <div style={{ fontSize: 11, color: '#b91c1c', fontWeight: 700 }}>{isAr ? 'تاريخ النهاية لازم يكون بعد تاريخ البداية' : 'End date must be after the start date'}</div>}
                    <div style={{ background: '#fff', borderRadius: 10, border: '1px dashed #cbd5e1' }}>
                        <ProjectAttachmentsField
                            id={`sprint_attachments_${sprint?._id || 'new'}`}
                            value={attachments}
                            names={attachmentNameMap}
                            onChange={setAttachments}
                            onNamesChange={(uploaded) => setAttachmentNameMap((current) => ({ ...current, ...uploaded }))}
                            onBusyChange={setAttachmentsBusy}
                            lang={isAr ? 'ar' : 'en'}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px', borderTop: '1px solid #e2e8f0' }}>
                    <MwButton type="cancelBtn" withIcon={false} onClick={closeForm}>{isAr ? 'إلغاء' : 'Cancel'}</MwButton>
                    <MwButton
                        type="saveBtn"
                        disabled={saving || datesInvalid || attachmentsBusy}
                        onClick={() => onSave({
                            title: title.trim() || titleEn.trim(),
                            description: description.trim() || descriptionEn.trim(),
                            titleEn: titleEn.trim(),
                            descriptionEn: descriptionEn.trim(),
                            attachments,
                            attachmentNames: toAttachmentNamesPayload(attachmentNameMap, attachments),
                            // نهاية الدورة = آخر اليوم المختار.
                            startAt: startAt ? new Date(`${startAt}T00:00:00`).toISOString() : null,
                            endAt: endAt ? new Date(`${endAt}T23:59:59`).toISOString() : null
                        })}
                    >
                        {saving ? (isAr ? 'جاري الحفظ...' : 'Saving...') : (isAr ? 'حفظ' : 'Save')}
                    </MwButton>
                </div>
            </div>
        </div>
    );
};

const SprintCard = ({ sprint, isAr, statuses, active, canManage, onSelect, onEdit, onDelete, onAddTicket, nowMs }) => {
    const total = Number(sprint.ticketsCount || 0);
    const done = countDone(sprint.statusCounts, statuses);
    const percent = total ? Math.round((done / total) * 100) : 0;
    const phase = SPRINT_PHASE_META[getSprintPhase(sprint, done, nowMs)];
    const iconBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer' };
    const sprintTitle = localizedText(sprint, 'title', isAr ? 'ar' : 'en');
    const sprintDescription = localizedText(sprint, 'description', isAr ? 'ar' : 'en');
    const sprintFiles = normalizeAttachmentList(sprint.attachments);

    return (
        <div
            onClick={onSelect}
            style={{
                cursor: 'pointer',
                borderRadius: 14,
                border: active ? '2px solid #4f46e5' : '1px solid #e2e8f0',
                background: active ? '#f5f7ff' : '#fff',
                padding: active ? 11 : 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                minWidth: 0,
                transition: 'border-color .15s, box-shadow .15s',
                boxShadow: active ? '0 4px 16px rgba(79,70,229,.12)' : 'none'
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, background: '#ecfeff', color: '#0e7490', fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap' }}>
                    <FiFlag size={11} /> {isAr ? 'دورة' : 'Sprint'} {sprint.sprintNumber}
                </span>
                <span style={{ padding: '3px 9px', borderRadius: 999, background: phase.bg, color: phase.color, fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }}>
                    {isAr ? phase.ar : phase.en}
                </span>
                <span style={{ flex: 1 }} />
                {canManage && (
                    <span style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                        <button type="button" title={isAr ? 'تذكرة جديدة في الدورة' : 'New ticket in sprint'} style={iconBtn} onClick={onAddTicket}><FiPlus size={13} /></button>
                        <button type="button" title={isAr ? 'تعديل' : 'Edit'} style={iconBtn} onClick={onEdit}><FiEdit2 size={12} /></button>
                        <button type="button" title={isAr ? 'حذف' : 'Delete'} style={{ ...iconBtn, color: '#dc2626' }} onClick={onDelete}><FiTrash2 size={12} /></button>
                    </span>
                )}
            </div>
            {sprintTitle && <div dir="auto" style={{ fontSize: 13, fontWeight: 900, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sprintTitle}>{sprintTitle}</div>}
            {sprintDescription && (
                <div dir="auto" title={sprintDescription} style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-line' }}>
                    {sprintDescription}
                </div>
            )}
            {sprintFiles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                    {sprintFiles.slice(0, 3).map((url) => {
                        const info = getAttachmentInfo(url, sprint.attachmentNames);
                        return (
                            <a key={url} href={url} target="_blank" rel="noreferrer" title={info.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 150, padding: '2px 8px', borderRadius: 999, background: '#f1f5f9', color: '#334155', fontSize: 10, fontWeight: 800, textDecoration: 'none' }}>
                                <FiPaperclip size={10} style={{ flexShrink: 0 }} />
                                <span dir="auto" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.name}</span>
                            </a>
                        );
                    })}
                    {sprintFiles.length > 3 && <span style={{ padding: '2px 8px', borderRadius: 999, background: '#f1f5f9', color: '#64748b', fontSize: 10, fontWeight: 800 }}>+{sprintFiles.length - 3}</span>}
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#334155', fontWeight: 700 }}>
                <FiCalendar size={12} color="#94a3b8" />
                <span>{formatDate(sprint.startAt)}</span>
                <span style={{ color: '#94a3b8' }}>{isAr ? '←' : '→'}</span>
                <span>{formatDate(sprint.endAt)}</span>
            </div>
            <div>
                {/* الإنجاز: المكتملة والمغلقة من كل تذاكر الدورة، والشريط بيتملى أخضر بنسبتها. */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 11, fontWeight: 800, color: '#475569', marginBottom: 5 }}>
                    <span data-testid="sprint-progress-label">
                        {isAr ? 'الإنجاز' : 'Done'}: <span style={{ color: '#15803d' }}>{done}</span> {isAr ? 'من' : 'of'} {total} {isAr ? 'تذكرة' : 'tickets'}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 900, color: percent === 100 ? '#0f766e' : percent > 0 ? '#15803d' : '#94a3b8' }}>{percent}%</span>
                </div>
                <div
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={isAr ? 'نسبة إنجاز الدورة' : 'Sprint progress'}
                    style={{ height: 8, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}
                >
                    <div style={{ width: `${percent}%`, height: '100%', borderRadius: 999, background: percent === 100 ? '#0d9488' : 'linear-gradient(90deg, #22c55e, #16a34a)', transition: 'width .3s' }} />
                </div>
            </div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>
                {isAr ? 'أُنشئت' : 'Created'} {formatDate(sprint.createdAt)}{sprint.createdByName ? ` · ${sprint.createdByName}` : ''}
            </div>
        </div>
    );
};

// دورات المشروع: إنشاء / تعديل / حذف، واختيار دورة بيفلتر تذاكر المشروع تحتها.
const ProjectSprintsPanel = ({ projectId, isAr, statuses, sprintsResult, loading, error = null, canEditProject = false, onChanged, selectedSprint, onSelectSprint, totalTickets = 0, nowMs }) => {
    const navigate = useNavigate();
    const params = useParams();
    const [formSprint, setFormSprint] = useState(null); // null = مقفول، {} = جديد، sprint = تعديل
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const sprints = useMemo(() => (Array.isArray(sprintsResult?.data) ? sprintsResult.data : []), [sprintsResult]);
    // الصلاحية من السيرفر، ولو لسه ما رجعتش نعتمد على صلاحية تعديل المشروع.
    const canManage = sprintsResult?.meta?.canManage ?? canEditProject;
    const unassignedCount = Number(sprintsResult?.meta?.unassignedTicketsCount || 0);
    const nextNumber = sprints.reduce((max, item) => Math.max(max, Number(item.sprintNumber || 0)), 0) + 1;
    const baseUrl = `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin/${projectId}/sprints`;

    const saveSprint = async (payload) => {
        setSaving(true);
        setFormError(null);
        try {
            const isEdit = Boolean(formSprint?._id);
            const config = buildAuthRequestConfig({ validateStatus: () => true });
            const response = isEdit
                ? await apiClient.put(`${baseUrl}/${formSprint._id}`, payload, config)
                : await apiClient.post(baseUrl, payload, config);
            if (response?.data?.status !== 1) {
                setFormError(response?.data?.message || (isAr ? 'تعذر حفظ الدورة' : 'Failed to save sprint'));
                return;
            }
            toast.success(isEdit ? (isAr ? 'تم تعديل الدورة' : 'Sprint updated') : (isAr ? 'تم إنشاء الدورة' : 'Sprint created'));
            setFormSprint(null);
            onChanged?.();
        } catch {
            setFormError(isAr ? 'تعذر حفظ الدورة' : 'Failed to save sprint');
        } finally {
            setSaving(false);
        }
    };

    const deleteSprint = async () => {
        if (!deleteTarget?._id) return;
        setDeleting(true);
        try {
            const response = await apiClient.delete(`${baseUrl}/${deleteTarget._id}`, buildAuthRequestConfig({ validateStatus: () => true }));
            if (response?.data?.status !== 1) {
                toast.error(response?.data?.message || (isAr ? 'تعذر حذف الدورة' : 'Failed to delete sprint'));
                return;
            }
            const files = normalizeAttachmentList(deleteTarget.attachments);
            if (files.length) deleteUploadedFiles(files).catch(() => {});
            toast.success(isAr ? 'تم حذف الدورة' : 'Sprint deleted');
            if (selectedSprint === String(deleteTarget._id)) onSelectSprint?.('all');
            setDeleteTarget(null);
            onChanged?.();
        } finally {
            setDeleting(false);
        }
    };

    const filterChip = (value, label, count) => {
        const isActive = selectedSprint === value;
        return (
            <button
                key={value}
                type="button"
                onClick={() => onSelectSprint?.(value)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, border: isActive ? '1px solid #4f46e5' : '1px solid #e2e8f0', background: isActive ? '#eef2ff' : '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 800, color: '#0f172a' }}
            >
                {label}
                <span style={{ minWidth: 22, padding: '0 7px', borderRadius: 999, background: '#f1f5f9', color: '#334155', fontSize: 11, fontWeight: 900 }}>{count}</span>
            </button>
        );
    };

    return (
        <div className="admin-panel p-3 md:p-4">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{isAr ? 'دورات المشروع (Sprints)' : 'Project sprints'}</span>
                <span style={{ minWidth: 26, padding: '2px 8px', borderRadius: 999, background: '#ecfeff', color: '#0e7490', fontSize: 11, fontWeight: 900, textAlign: 'center' }}>{sprints.length}</span>
                <span style={{ flex: 1 }} />
                {canManage && (
                    <MwButton type="secondary" onClick={() => { setFormError(null); setFormSprint({}); }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FiPlus size={14} /> {isAr ? 'إنشاء دورة' : 'New sprint'}</span>
                    </MwButton>
                )}
            </div>

            {error && !sprintsResult ? (
                <div style={{ padding: '12px 16px', borderRadius: 12, border: '1px solid #fecaca', background: '#fef2f2', fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>
                    {error?.response?.status === 404
                        ? (isAr ? 'خدمة الدورات غير متاحة على السيرفر — تأكد من نشر آخر إصدار من api-server-v2.' : 'Sprints API not found on the server — deploy the latest api-server-v2.')
                        : (isAr ? 'تعذر تحميل الدورات' : 'Failed to load sprints')}
                </div>
            ) : loading && !sprintsResult ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}><MwSpinnerButton withLabel={false} isFullCenter /></div>
            ) : sprints.length === 0 ? (
                <div style={{ padding: '14px 16px', borderRadius: 12, border: '1px dashed #cbd5e1', background: '#f8fafc', fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                    {isAr
                        ? 'لا توجد دورات بعد. قسّم المشروع لمراحل (دورة 1، دورة 2...) واربط التذاكر بكل مرحلة.'
                        : 'No sprints yet. Split the project into phases (Sprint 1, Sprint 2...) and link tickets to each.'}
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                        {filterChip('all', isAr ? 'كل التذاكر' : 'All tickets', totalTickets)}
                        {filterChip('none', isAr ? 'بدون دورة' : 'No sprint', unassignedCount)}
                    </div>
                    <div className="table-scroll" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, maxHeight: 460, overflowY: 'auto', padding: 2 }}>
                        {sprints.map((sprint) => (
                            <SprintCard
                                key={sprint._id}
                                sprint={sprint}
                                isAr={isAr}
                                statuses={statuses}
                                nowMs={nowMs}
                                active={selectedSprint === String(sprint._id)}
                                canManage={canManage}
                                onSelect={() => onSelectSprint?.(selectedSprint === String(sprint._id) ? 'all' : String(sprint._id))}
                                onEdit={() => { setFormError(null); setFormSprint(sprint); }}
                                onDelete={() => setDeleteTarget(sprint)}
                                onAddTicket={() => navigate(`/${params.tenant}/tickets/add?projectId=${projectId}&sprintId=${sprint._id}`)}
                            />
                        ))}
                    </div>
                </>
            )}

            {formSprint && (
                <SprintFormModal
                    key={formSprint._id || 'new'}
                    isAr={isAr}
                    sprint={formSprint._id ? formSprint : null}
                    nextNumber={nextNumber}
                    saving={saving}
                    error={formError}
                    onClose={() => setFormSprint(null)}
                    onSave={saveSprint}
                />
            )}

            <ConfirmDelete
                title={isAr ? 'حذف الدورة' : 'Delete sprint'}
                msg={isAr
                    ? `سيتم حذف الدورة ${deleteTarget?.sprintNumber ?? ''}. التذاكر المرتبطة بها ستبقى في المشروع لكن بدون دورة.`
                    : `Sprint ${deleteTarget?.sprintNumber ?? ''} will be deleted. Its tickets stay in the project without a sprint.`}
                onShow={Boolean(deleteTarget)}
                setOnShow={(value) => { if (!value) setDeleteTarget(null); }}
                loading={deleting}
                onDeleteHandler={deleteSprint}
                confirmLabel={isAr ? 'حذف الدورة' : 'Delete sprint'}
            />
        </div>
    );
};

export default ProjectSprintsPanel
